import {t} from 'i18next';
import {useEffect, useRef, useState, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  getProcessingExceptionsAlerts,
  updateAlertStatusAsViewed,
} from '../../../../api/UserManagementService';
import {setAlerts} from '../../../../redux/slices/alertSlice';
import {RootState} from '../../../../redux/store';
import AurAlertCarousel from '../../../common/aur-alert/AurAlertCarousel';
import ReportSideBar from './ReportSideBar';
import ReportTableContent from './ReportTableContent';
import {ReportTypes} from './model';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {getProcessingExceptionReportCount} from '../../../../api/ReportingManagement';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {failedToLoadStatusCodes} from '../../../../constants/constants';
import {
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../../hooks/permissions/usePermissionsByPage';
import isBoolean from 'lodash/isBoolean';

interface ProcessingExceptionsProps {
  skipTabTracking?: boolean;
  setActiveReport: (reportType: string) => void;
}

const abortControllers: Map<string, AbortController> = new Map();

const ProcessingExceptions: React.FC<ProcessingExceptionsProps> = ({
  skipTabTracking = false,
  setActiveReport,
}) => {
  const {trackGAEvent} = useAnalytics();
  const [activeItem, setActiveItem] = useState<string>('');
  const [toggleAlertState, setToggleAlertState] = useState(false);
  const [isExceptionCountLoading, setIsExceptionCountLoading] = useState(false);
  const [isExceptionCountReqFailed, setIsExceptionCountReqFailed] =
    useState(false);
  const apiResponseTime = useRef('');

  const [reportNavigationList, setReportNavigationList] = useState([
    {
      groupName: t('reportGroups.actionableExceptions'),
      items: [
        {
          name: ReportTypes.CORRUPTED_ACTIONABLE,
          count: 0,
        },
        {
          name: ReportTypes.ENCRYPTED,
          count: 0,
        },
      ],
    },
    {
      groupName: t('reportGroups.alreadyResolved'),
      items: [
        {
          name: ReportTypes.NON_SEARCHABLE_PDFS,
          count: 0,
        },
        {
          name: ReportTypes.CORRECTED_BAD_EXTENSIONS,
          count: 0,
        },
        {
          name: ReportTypes.RECOVERED_DELETED_EMAILS,
          count: 0,
        },
      ],
    },
    {
      groupName: t('reportGroups.nonActionableExceptions'),
      items: [
        {
          name: ReportTypes.CORRUPTED_NON_ACTIONABLE,
          count: 0,
        },
        {
          name: ReportTypes.EMPTY_ZERO_BYTE_FILES,
          count: 0,
        },
        {
          name: ReportTypes.PROBLEMATIC_TEXT,
          count: 0,
        },
        {
          name: ReportTypes.UNRECOGNIZED_FILE,
          count: 0,
        },
        {
          name: ReportTypes.UNSUPPORTED_FILE,
          count: 0,
        },
      ],
    },
  ]);

  const alertData = useSelector((state: RootState) => state.alerts);
  const dispatch = useDispatch();
  const [isAlertLoading, setIsAlertLoading] = useState(false);
  const [isFailedToLoadAlert, setIsFailedToLoadAlert] =
    useState<boolean>(false);
  const {clientId, projectId, workspaceId} = useSelector(
    (state: RootState) => state.reports
  );

  const dataSetName = useSelector(
    (state: RootState) => state.reports.workspaceDataSetName
  );

  const {canViewProcessingSummaryReports, canViewProcessingExceptionReports} =
    usePermissionsByPage(PAGE_NAME.PROCESSING_SUMMARY_EXCEPTIONS_TAB);

  const isBoolPerm = useMemo(
    () =>
      isBoolean(canViewProcessingSummaryReports) &&
      isBoolean(canViewProcessingExceptionReports),
    [canViewProcessingSummaryReports, canViewProcessingExceptionReports]
  );

  useEffect(() => {
    // Clean up
    return () => {
      abortControllers.forEach((controller) => {
        isBoolPerm &&
          (canViewProcessingSummaryReports ||
            canViewProcessingExceptionReports) &&
          controller.abort();
      });
      abortControllers.clear();
    };
  }, [
    canViewProcessingSummaryReports,
    canViewProcessingExceptionReports,
    isBoolPerm,
  ]);

  useEffect(() => {
    setActiveReport(activeItem);
  }, [activeItem]);

  useEffect(() => {
    if (projectId && clientId && workspaceId && dataSetName) {
      fecthExceptionReportCount();
    }
  }, [clientId, projectId, workspaceId, dataSetName]);

  useEffect(() => {
    let found = false;
    reportNavigationList.forEach((group) => {
      if (!found) {
        const item = group.items.find((i) => i.count > 0);
        if (item) {
          setActiveItem(item.name);
          found = true;
        }
      }
    });
  }, [reportNavigationList]);

  useEffect(() => {
    if (projectId && clientId && workspaceId)
      isBoolPerm &&
        (canViewProcessingExceptionReports ||
          (!canViewProcessingSummaryReports &&
            !canViewProcessingExceptionReports)) &&
        fetchExceptionsAlertsData();
  }, [
    clientId,
    projectId,
    workspaceId,
    toggleAlertState,
    canViewProcessingSummaryReports,
    canViewProcessingExceptionReports,
    isBoolPerm,
  ]);

  const fecthExceptionReportCount = async () => {
    const url = '/processing-exceptions/counts';
    if (clientId && projectId && workspaceId && dataSetName) {
      try {
        if (abortControllers.has(url)) {
          abortControllers.get(url)?.abort();
        }

        const controller = new AbortController();
        abortControllers.set(url, controller);

        setIsExceptionCountReqFailed(false);
        setIsExceptionCountLoading(true);
        const response = await getProcessingExceptionReportCount(
          clientId,
          projectId,
          workspaceId,
          dataSetName,
          controller.signal
        );
        if (activeItem === '') setActiveItem(ReportTypes.CORRUPTED_ACTIONABLE);
        abortControllers.delete(url);
        updateReportCount(response?.data?.data[0] || {});
        setIsExceptionCountLoading(false);
      } catch (e: unknown) {
        if ((e as {name: string}).name === 'CanceledError') {
          return;
        }
        abortControllers.delete(url);
        setIsExceptionCountReqFailed(true);
        setIsExceptionCountLoading(false);
      }
    }
  };

  const fetchExceptionsAlertsData = async () => {
    if (clientId && projectId && workspaceId) {
      const url = '/alerts/processing-exceptions';

      if (abortControllers.has(url)) {
        abortControllers.get(url)?.abort();
      }

      const controller = new AbortController();
      abortControllers.set(url, controller);

      try {
        setIsAlertLoading(true);
        setIsFailedToLoadAlert(false);
        const responseFetchAlert = await getProcessingExceptionsAlerts(
          clientId,
          projectId,
          workspaceId,
          controller.signal
        );
        abortControllers.delete(url);
        dispatch(setAlerts(responseFetchAlert?.data?.data));
        setIsAlertLoading(false);
        apiResponseTime.current = responseFetchAlert.data.responseTime;
      } catch (error: unknown) {
        const e = error as {name: string; status: number};
        if (e.name === 'CanceledError') {
          return;
        }
        if (failedToLoadStatusCodes.includes(e.status))
          setIsFailedToLoadAlert(true);
        setIsAlertLoading(false);
        abortControllers.delete(url);
        apiResponseTime.current = (error as any).responseTime;
      } finally {
        !skipTabTracking &&
          trackGAEvent({
            eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
            subPage: {
              subpage_name: t('gaTracking.reports.processingExceptions'),
              subpage_type: GA_SUBPAGE_TYPE.TAB,
            },
            user: GA_DATA_MODE_AUTO,
          });
      }
    }
  };

  const markAlertAsViewed = (userAlertId: number) => {
    updateAlertStatusAsViewed(userAlertId);
    setToggleAlertState((prevState) => !prevState);
  };

  const updateReportCount = (data: any) => {
    setReportNavigationList((prevList) => {
      const updatedList = prevList.map((group) => {
        // Update the group items based on the API response
        return {
          ...group,
          items: group.items.map((item) => {
            switch (item.name) {
              case ReportTypes.CORRUPTED_ACTIONABLE:
                return {...item, count: data.corruptedActionable};
              case ReportTypes.ENCRYPTED:
                return {...item, count: data.encrypted};
              case ReportTypes.NON_SEARCHABLE_PDFS:
                return {...item, count: data.nonSearchablePDFs};
              case ReportTypes.CORRECTED_BAD_EXTENSIONS:
                return {...item, count: data.badExtension};
              case ReportTypes.RECOVERED_DELETED_EMAILS:
                return {...item, count: data.deleted};
              case ReportTypes.CORRUPTED_NON_ACTIONABLE:
                return {...item, count: data.corruptedNonActionable};
              case ReportTypes.EMPTY_ZERO_BYTE_FILES:
                return {...item, count: data.empty};
              case ReportTypes.PROBLEMATIC_TEXT:
                return {...item, count: data.textStripped};
              case ReportTypes.UNRECOGNIZED_FILE:
                return {...item, count: data.unrecognized};
              case ReportTypes.UNSUPPORTED_FILE:
                return {...item, count: data.unsupportedItems};
              default:
                return item; // In case there are other items not in the API response
            }
          }),
        };
      });
      return updatedList;
    });
  };
  const renderAlert = () => {
    if (isExceptionCountLoading) return;

    if (isAlertLoading)
      return (
        <div className="exception-alert-loading">
          <AurLoadingIndicator />
        </div>
      );

    if (isFailedToLoadAlert)
      return (
        <div className="exception-alert-error">
          <AurDataGridErrorOverlay
            onRefresh={fetchExceptionsAlertsData}
            enableParentWrapper={false}
          />
        </div>
      );

    return alertData?.alerts?.length > 0 ? (
      <div className="processing-exception-alert">
        <AurAlertCarousel
          alertsData={
            alertData?.alerts.map((alert) => ({
              ...alert,
              deleteButtonHandler: () => markAlertAsViewed(alert.userAlertId),
            })) as []
          }
        />
      </div>
    ) : null;
  };

  const renderExceptions = () => {
    if (isExceptionCountLoading)
      return (
        <div className="exception-alert-loading">
          <AurLoadingIndicator />
        </div>
      );

    if (isExceptionCountReqFailed) {
      return (
        <div className="processing-exception-report-content">
          <AurDataGridErrorOverlay
            onRefresh={fecthExceptionReportCount}
            enableParentWrapper={false}
          />
        </div>
      );
    }

    if (activeItem !== '')
      return (
        <div className="processing-exception-report-content">
          <div className="processing-exception-report-sidebar-content">
            <ReportSideBar
              sidebarData={reportNavigationList}
              activeItemName={activeItem}
              setActiveItem={setActiveItem}
            />
          </div>
          <div className="processing-exception-report-table">
            <ReportTableContent activeReport={activeItem} />
          </div>
        </div>
      );
    return null;
  };

  return (
    <div className="processing-exception-content">
      <div
        className="processing-exception-description"
        data-testid="processing-exception-desc"
      >
        {t('reports.processingExcception.description')}
      </div>
      {renderAlert()}
      {renderExceptions()}
    </div>
  );
};

export default ProcessingExceptions;
